// components/portal/FloorManagement.tsx
'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { LeasingFloorDTO } from '#types/types';
import { toast } from 'sonner';
import { useKernelStore } from '@/kernel/kernelStores';

const translations = {
  en: {
    floorNamePrompt: 'Floor Name (e.g. Level 2):',
    levelNumberPrompt: 'Level Number (e.g. 2):',
    floorCreated: 'Floor created',
    floorCreateFailed: 'Failed to create floor',
    floorNamePromptEdit: 'Floor Name:',
    levelNumberPromptEdit: 'Level Number:',
    surfacePrompt: 'Surface (m2):',
    floorUpdated: 'Floor updated',
    floorUpdateFailed: 'Failed to update floor',
    deleteFloorConfirm: 'Delete this floor?',
    floorDeleted: 'Floor deleted',
    floorDeleteFailed: 'Failed to delete floor',
    uploadingPlan: 'Uploading floor plan...',
    floorPlanUpdated: 'Floor plan updated',
    floorPlanUploadFailed: 'Failed to upload floor plan',
  },
  fr: {
    floorNamePrompt: 'Nom de l\'etage (ex. Niveau 2):',
    levelNumberPrompt: 'Numero de niveau (ex. 2):',
    floorCreated: 'Etage cree',
    floorCreateFailed: 'Echec creation etage',
    floorNamePromptEdit: 'Nom de l\'etage:',
    levelNumberPromptEdit: 'Numero de niveau:',
    surfacePrompt: 'Surface (m2):',
    floorUpdated: 'Etage mis a jour',
    floorUpdateFailed: 'Echec mise a jour etage',
    deleteFloorConfirm: 'Supprimer cet etage ?',
    floorDeleted: 'Etage supprime',
    floorDeleteFailed: 'Echec suppression etage',
    uploadingPlan: 'Envoi du plan...',
    floorPlanUpdated: 'Plan mis a jour',
    floorPlanUploadFailed: 'Echec envoi du plan',
  },
};

export function useFloorManagement(propertyId?: string | null) {
    const { kernel } = useKernel();
    const lang = useKernelStore(state => state.language);
    const t = translations[lang];
    const [uploadingFloorId, setUploadingFloorId] = useState<string | null>(null);
    const floorPlanInputRef = useRef<HTMLInputElement>(null);
    const uploadingFloorIdRef = useRef<string | null>(null);

    useEffect(() => {
        uploadingFloorIdRef.current = uploadingFloorId;
    }, [uploadingFloorId]);

    const handleAddFloor = useCallback(async () => {
        if (!propertyId) return;
        const name = window.prompt(t.floorNamePrompt);
        if (!name) return;
        
        const levelStr = window.prompt(t.levelNumberPrompt, "0");
        const level = parseInt(levelStr || "0", 10);
        if (isNaN(level)) return;
        const k = kernel.current;
        if (!k) return;

        try {
            await k.mutateInventory([{
                action: 'CREATE',
                entity: 'Floor',
                payload: { propertyId, name, level }
            }], 'Create floor');
            toast.success(t.floorCreated);
        } catch (e) {
            toast.error(t.floorCreateFailed);
        }
    }, [kernel, propertyId, t]);

    const handleEditFloor = useCallback(async (floor: LeasingFloorDTO) => {
        const name = window.prompt(t.floorNamePromptEdit, floor.name);
        if (name === null) return;
        
        const levelStr = window.prompt(t.levelNumberPromptEdit, String(floor.level));
        const level = parseInt(levelStr || "0", 10);
        
        const surfaceStr = window.prompt(t.surfacePrompt, floor.surface ? String(floor.surface) : "");
        const surface = surfaceStr ? parseFloat(surfaceStr) : null;
        const k = kernel.current;
        if (!k) return;

        try {
            await k.mutateInventory([{
                action: 'UPDATE',
                entity: 'Floor',
                id: floor.id,
                payload: { name, level, surface }
            }], 'Update floor');
            toast.success(t.floorUpdated);
        } catch (e) {
            toast.error(t.floorUpdateFailed);
        }
    }, [kernel, t]);

    const handleDeleteFloor = useCallback(async (floorId: string) => {
        if (!window.confirm(t.deleteFloorConfirm)) return;
        const k = kernel.current;
        if (!k) return;
        try {
            await k.mutateInventory([{
                action: 'DELETE',
                entity: 'Floor',
                id: floorId
            }], 'Delete floor');
            toast.success(t.floorDeleted);
        } catch (e) {
            toast.error(t.floorDeleteFailed);
        }
    }, [kernel, t]);

    const handleUploadPlan = useCallback((floorId: string) => {
        uploadingFloorIdRef.current = floorId;
        setUploadingFloorId(floorId);
        setTimeout(() => floorPlanInputRef.current?.click(), 0);
    }, []);

    const handleFloorPlanFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const targetFloorId = uploadingFloorIdRef.current;
        if (!file || !targetFloorId) return;

        const toastId = toast.loading(t.uploadingPlan);
        try {
            await kernel.current?.uploadFloorPlan(targetFloorId, file);
            toast.success(t.floorPlanUpdated);
        } catch (err) {
            console.error(err);
            toast.error(t.floorPlanUploadFailed);
        } finally {
            toast.dismiss(toastId);
            uploadingFloorIdRef.current = null;
            setUploadingFloorId(null);
            if (floorPlanInputRef.current) floorPlanInputRef.current.value = '';
        }
    }, [kernel, t]);

    return useMemo(() => ({
        handleAddFloor,
        handleEditFloor,
        handleDeleteFloor,
        handleUploadPlan,
        handleFloorPlanFileChange,
        floorPlanInputRef,
        uploadingFloorId
    }), [
        handleAddFloor,
        handleEditFloor,
        handleDeleteFloor,
        handleUploadPlan,
        handleFloorPlanFileChange,
        uploadingFloorId
    ]);
}
